import { useEffect, useState, type FormEvent } from 'react';

import {
  CategoryDTO,
  EntityRefUtil,
  formatDateWithUserTimezone,
  IssueNotificationRuleDTO,
  NotificationRuleDTO,
  NotificationType,
  ScheduleNotificationRuleDTO,
} from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';

import CalendarBlankIcon from '@/components/icons/calendar-blank';
import ClockIcon from '@/components/icons/clock';
import CloneIcon from '@/components/icons/clone';
import DeleteIcon from '@/components/icons/delete';
import FolderClosedIcon from '@/components/icons/folder-closed';
import FormChecklistIcon from '@/components/icons/form-checklist';
import PlusIcon from '@/components/icons/plus';
import QuestionIcon from '@/components/icons/question';
import WarningTriangleIcon from '@/components/icons/warning-triangle';
import { NotificationsTabSkeleton } from '@/components/settings/settings-form-skeleton';
import { Badge } from '@/components/ui/badge';
import { ButtonStateToggle } from '@/components/ui/button-state-toggle';
import { Label } from '@/components/ui/label';
import { CreatableMultiSelect } from '@/components/ui/multi-select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { useToast } from '@/components/ui/toaster';
import { Tooltip, TooltipArrow, TooltipContent, TooltipPortal, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { usePermissions } from '@/hooks/use-permissions';
import { Utils } from '@/legacy/utils';
import { getDocumentCategoriesQueryOptions } from '@/queries/documents';
import {
  getIssueNotificationRulesQueryOptions,
  getLegacyComplianceNotificationsQueryOptions,
  getLegacyInspectionNotificationsQueryOptions,
  getScheduleNotificationRulesQueryOptions,
  issueNotificationRuleKeys,
  scheduleNotificationRuleKeys,
} from '@/queries/notifications';
import { ApiService } from '@/services/api-v2';
import { useUserStore } from '@/stores/user.store';
import { SEVERITY_BADGE_VARIANT } from '@/utils/issue';

import { NotificationRuleSheet } from './notification-rule-sheet';
import { ScheduleNotificationRuleSheet } from './schedule-notification-rule-sheet';
import { NotificationsWorkOrder } from './work-order-notifications-form';

const PERIOD_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 21, 30, 35, 40, 45, 50, 60, 70, 80, 90, 120, 150, 180, 210];

function SettingsNotificationsPage() {
  return (
    <div className="flex w-full max-w-[833px] flex-col gap-4 rounded-lg bg-white p-6 shadow-lg">
      <ScrollArea viewportClassName="pr-2">
        <div className="flex flex-col gap-4">
          <h1 className="text-default-text text-xl font-medium">Notifications</h1>
          <NotificationsTabs />
        </div>
      </ScrollArea>
    </div>
  );
}

function NotificationsTabs() {
  const [tab, setTab] = useState('compliance');
  const { data: complianceData, isLoading: loadingCompliance } = useQuery(getLegacyComplianceNotificationsQueryOptions());

  if (loadingCompliance || !complianceData) {
    return <NotificationsTabSkeleton />;
  }

  return (
    <Tabs value={tab} onValueChange={setTab} className="flex w-full flex-col gap-4">
      <ButtonStateToggle
        options={[
          { icon: <FolderClosedIcon />, label: 'Documents', value: 'compliance' },
          { icon: <FormChecklistIcon />, label: 'Inspections', value: 'inspections' },
          { icon: <WarningTriangleIcon />, label: 'Issues', value: 'issues' },
          { icon: <ClockIcon />, label: 'Schedules', value: 'schedules' },
          { icon: <CalendarBlankIcon />, label: 'Work Orders', value: 'work-orders' },
        ]}
        value={tab}
        onChange={setTab}
      />

      <TabsContent value="compliance">
        <NotificationsCompliance emails={complianceData.emails} rules={complianceData.rules} />
      </TabsContent>

      <TabsContent value="inspections">
        <NotificationsInspection />
      </TabsContent>

      <TabsContent value="issues">
        <NotificationsMaintenance type={NotificationType.Issue} />
      </TabsContent>

      <TabsContent value="schedules">
        <NotificationsSchedule />
      </TabsContent>

      <TabsContent value="work-orders">
        <NotificationsWorkOrder />
      </TabsContent>
    </Tabs>
  );
}

function NotificationsMaintenance({ type }: { type: NotificationType }) {
  const queryClient = useQueryClient();
  const { canEditWorkspaceSettings } = usePermissions();

  const [selectedRule, setSelectedRule] = useState<IssueNotificationRuleDTO | null>(null);
  const [showRulePanel, setShowRulePanel] = useState<NotificationType | null>(null);

  const { data: rules = [], isLoading: loading } = useQuery(getIssueNotificationRulesQueryOptions({ type }));

  function handleOpenRulePanel(rule?: IssueNotificationRuleDTO) {
    setSelectedRule(rule || null);
    setShowRulePanel(type);
  }

  function handleCloseRulePanel(refresh?: boolean) {
    setShowRulePanel(null);
    setSelectedRule(null);
    if (refresh) {
      queryClient.invalidateQueries({ queryKey: issueNotificationRuleKeys.byType(type) });
    }
  }

  if (loading) {
    return <NotificationsTabSkeleton />;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-base font-medium">{type === NotificationType.Issue ? 'Issue Notifications' : 'Legacy Schedule Notifications'}</h2>
        {type === NotificationType.Issue && (
          <p className="text-default-text text-sm">
            Below are the rules used to process new issue. Click on the rule's name to edit or click 'Add Rule' to create a new rule.
          </p>
        )}
        {type === NotificationType.Servicing && (
          <p className="text-default-text text-sm">
            Below you can create a rules which will generate a daily summary of all your services that match the set parameters. This report will be sent each
            night to the relevant contacts
          </p>
        )}
      </div>

      <div className="flex flex-col gap-2">
        {rules.length > 0 ? (
          rules.map((rule) => (
            <NotificationRuleCard key={rule.id} rule={rule} type={type} onEdit={canEditWorkspaceSettings ? () => handleOpenRulePanel(rule) : undefined} />
          ))
        ) : (
          <p className="text-secondary-text text-sm">No notification rules have been created yet.</p>
        )}

        {canEditWorkspaceSettings && (
          <Button className="w-fit" onClick={() => handleOpenRulePanel()} icon={<PlusIcon />}>
            Add Rule
          </Button>
        )}
      </div>

      <NotificationRuleSheet open={showRulePanel !== null} onClose={handleCloseRulePanel} rule={selectedRule} type={type} />
    </div>
  );
}

function NotificationsSchedule() {
  const queryClient = useQueryClient();
  const { canEditWorkspaceSettings } = usePermissions();

  const [selectedRuleId, setSelectedRuleId] = useState<string | null>(null);
  const [showRulePanel, setShowRulePanel] = useState(false);

  const { data, isLoading } = useQuery(getScheduleNotificationRulesQueryOptions());

  const rules = data?.data ?? [];

  function handleOpenRulePanel(ruleId?: string) {
    setSelectedRuleId(ruleId ?? null);
    setShowRulePanel(true);
  }

  function handleCloseRulePanel(refresh?: boolean) {
    setShowRulePanel(false);
    setSelectedRuleId(null);
    if (refresh) {
      queryClient.invalidateQueries({ queryKey: scheduleNotificationRuleKeys.all });
    }
  }

  if (isLoading) {
    return <NotificationsTabSkeleton />;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-base font-medium">Schedule Notifications</h2>
        <p className="text-default-text text-sm">
          Below you can create a rules which will generate a daily summary of all your schedules that match the set parameters.
        </p>
      </div>

      <p className="text-secondary-text text-sm">Schedule Check Notification Rule</p>

      <div className="flex flex-col gap-2">
        {rules.length > 0 ? (
          rules.map((rule) => (
            <ScheduleNotificationRuleCard key={rule.id} rule={rule} onEdit={canEditWorkspaceSettings ? () => handleOpenRulePanel(rule.id) : undefined} />
          ))
        ) : (
          <p className="text-secondary-text text-sm">No notification rules have been created yet.</p>
        )}

        {canEditWorkspaceSettings && (
          <Button className="w-fit" onClick={() => handleOpenRulePanel()} icon={<PlusIcon />}>
            Add Rule
          </Button>
        )}
      </div>

      <ScheduleNotificationRuleSheet open={showRulePanel} onClose={handleCloseRulePanel} ruleId={selectedRuleId} />
    </div>
  );
}

function ScheduleNotificationRuleCard({ rule, onEdit }: { rule: ScheduleNotificationRuleDTO; onEdit?: () => void }) {
  const userTimezone = useUserStore.getState().user?.timezone;
  const createdByName = EntityRefUtil.createUserFullNameRef(rule.createdBy);
  const createdDate = formatDateWithUserTimezone(new Date(rule.createdAt), userTimezone);

  return (
    <div className="flex flex-col gap-2 rounded border p-4">
      <div>
        {onEdit ? (
          <button onClick={onEdit} className="text-default-blue hover:text-default-extra-dark-blue text-base font-medium underline underline-offset-4">
            {rule.name}
          </button>
        ) : (
          <span className="text-base font-medium">{rule.name}</span>
        )}
      </div>
      <div className="text-sm">
        {createdByName && <p>Created by: {createdByName}</p>}
        <p>Date: {createdDate}</p>
      </div>
    </div>
  );
}

function NotificationRuleCard({ rule, type, onEdit }: { rule: IssueNotificationRuleDTO; type: NotificationType; onEdit?: () => void }) {
  return (
    <div className="rounded border p-4">
      <div className="mb-2 flex items-center gap-2">
        {onEdit ? (
          <button onClick={onEdit} className="text-default-blue hover:text-default-extra-dark-blue text-base font-medium underline underline-offset-4">
            {rule.name}
          </button>
        ) : (
          <span className="text-base font-medium">{rule.name}</span>
        )}
        {!rule.enabled && (
          <Badge variant="destructive" size="sm">
            Disabled
          </Badge>
        )}
      </div>

      {type === NotificationType.Issue && (
        <div className="text-default-text mb-3 text-sm">
          <span>When a new issue is detected </span>
          {rule.severities && (
            <>
              of{' '}
              <div className="inline-flex gap-1">
                {rule.severities.map((severity, i) => (
                  <Badge key={i} variant={SEVERITY_BADGE_VARIANT[severity]} size="sm">
                    {severity}
                  </Badge>
                ))}
              </div>{' '}
              severity
            </>
          )}
          {rule.tags && rule.tags.length > 0 && (
            <>
              {' '}
              for assets with tags{' '}
              <div className="inline-flex gap-1">
                {rule.tags.map((tag, i) => (
                  <Badge key={i} size="sm">
                    {tag}
                  </Badge>
                ))}
              </div>
            </>
          )}
          {rule.location && (
            <>
              {' '}
              in location <Badge size="sm">{rule.location.name}</Badge>
            </>
          )}
          , perform the following actions:
        </div>
      )}

      {type === NotificationType.Servicing && (
        <div className="text-default-text mb-3 text-sm">
          <span>When a new schedule is</span>
          {rule.serviceReading !== undefined && rule.serviceReading !== null && (
            <>
              <Badge className="mx-1" variant={rule.serviceReading > 0 ? 'default' : 'destructive'} size="sm">
                {rule.serviceReading > 0 ? 'due in' : 'overdue by'}
              </Badge>
              <strong>{rule.serviceReading < 0 ? rule.serviceReading * -1 : rule.serviceReading}</strong> {rule.serviceReadingUnit}
            </>
          )}
          {rule.serviceReading !== undefined && rule.serviceDate !== undefined && rule.serviceDate !== null && ' or '}
          {rule.serviceDate !== undefined && rule.serviceDate !== null && (
            <>
              <Badge className="mx-1" variant={rule.serviceDate > 0 ? 'default' : 'destructive'} size="sm">
                {rule.serviceDate > 0 ? 'due in' : 'overdue by'}
              </Badge>
              <strong>{rule.serviceDate < 0 ? rule.serviceDate * -1 : rule.serviceDate}</strong> days
            </>
          )}
          {rule.tags && (
            <>
              {' '}
              for assets with tags{' '}
              <div className="inline-flex gap-1">
                {rule.tags.map((tag, i) => (
                  <Badge key={i} size="sm">
                    {tag}
                  </Badge>
                ))}
              </div>
            </>
          )}
          {rule.location && (
            <>
              {' '}
              in location <Badge size="sm">{rule.location.name}</Badge>
            </>
          )}
          , perform the following actions:
        </div>
      )}

      <ul className="text-default-text space-y-1 text-sm">
        {rule?.emailLocation && <li>• Send an email to the location contact</li>}
        {rule?.emails && rule?.emails?.length > 0 && (
          <li>
            • Send an email to:{' '}
            <strong>
              {rule.emails?.map((email, i) => (
                <span key={i} className="text-default-blue">
                  {email}
                  {i < (rule.emails?.length ?? 0) - 1 && ', '}
                </span>
              ))}
            </strong>
          </li>
        )}
        {rule?.sms && rule?.sms?.length > 0 && (
          <li>
            • Send an SMS to:{' '}
            <strong>
              {rule.sms?.map((sms, i) => (
                <span key={i} className="text-default-blue">
                  {sms}
                  {i < (rule.sms?.length ?? 0) - 1 && ', '}
                </span>
              ))}
            </strong>
          </li>
        )}
      </ul>
    </div>
  );
}

const complianceEmailsSchema = z.object({
  emails: z.array(z.string().email()).default([]),
});
type ComplianceEmailsInput = z.input<typeof complianceEmailsSchema>;
type ComplianceEmailsOutput = z.output<typeof complianceEmailsSchema>;

const complianceFormSchema = z.object({
  rules: z
    .array(
      z.object({
        documentType: z.number().int().nonnegative().nullish(),
        categoryId: z.string().uuid().nullish(),
        interval: z.number().int().positive(),
      }),
    )
    .default([]),
});

type ComplianceFormInput = z.input<typeof complianceFormSchema>;
type ComplianceFormOutput = z.output<typeof complianceFormSchema>;

type ComplianceSaveResult = { settingName: string; succeeded: boolean };

function NotificationsCompliance({ emails, rules }: { emails: string[]; rules: NotificationRuleDTO[] }) {
  const toast = useToast();
  const { canEditWorkspaceSettings } = usePermissions();

  const emailsForm = useForm<ComplianceEmailsInput, unknown, ComplianceEmailsOutput>({
    resolver: zodResolver(complianceEmailsSchema),
    defaultValues: { emails },
  });

  const rulesForm = useForm<ComplianceFormInput, unknown, ComplianceFormOutput>({
    resolver: zodResolver(complianceFormSchema),
    defaultValues: { rules },
  });

  const { control: rulesControl, handleSubmit: handleSubmitRules, formState: rulesState, reset: resetRules } = rulesForm;
  const { control: emailsControl, handleSubmit: handleSubmitEmails, formState: emailsState, reset: resetEmails } = emailsForm;

  useEffect(() => {
    resetRules({ rules });
  }, [rules, resetRules]);

  useEffect(() => {
    resetEmails({ emails });
  }, [emails, resetEmails]);

  const { fields, append, remove, insert } = useFieldArray({
    control: rulesControl,
    name: 'rules',
  });

  const { org } = useUserStore();
  const { data: documentCategories = [] } = useQuery(getDocumentCategoriesQueryOptions({ orgId: org?.id }));

  const { mutateAsync: mutateEmailsAsync, isPending: isPendingEmails } = useMutation({
    mutationFn: async (emails: string[]) => {
      return await (await ApiService.UpdateNotifications({ type: NotificationType.Compliance, emails })).json();
    },
  });

  const { mutateAsync: mutateRulesAsync, isPending: isPendingRules } = useMutation({
    mutationFn: async (complianceRules: { documentType?: number | null; categoryId?: string | null; interval: number }[]) => {
      return await (
        await ApiService.UpdateNotificationRules({
          type: NotificationType.Compliance,
          rules: complianceRules.map((rule) => ({
            documentType: rule.categoryId ? null : (rule.documentType ?? null),
            categoryId: rule.categoryId ?? null,
            interval: rule.interval,
          })),
        })
      ).json();
    },
  });

  const saveEmails = async (data: ComplianceEmailsOutput): Promise<ComplianceSaveResult> => {
    try {
      const result = await mutateEmailsAsync(data.emails);
      const updatedEmails = result.notifications.filter((n) => n.type === NotificationType.Compliance).map((n) => n.email);
      resetEmails({ emails: updatedEmails });
      return { settingName: 'email notifications', succeeded: true };
    } catch (error) {
      console.error('Error updating emails:', error);
      return { settingName: 'email notifications', succeeded: false };
    }
  };

  const saveRules = async (data: ComplianceFormOutput): Promise<ComplianceSaveResult> => {
    try {
      const result = await mutateRulesAsync(data.rules);
      resetRules({ rules: result.rules.map((rule) => ({ documentType: rule.documentType, categoryId: rule.categoryId, interval: rule.interval })) });
      return { settingName: 'notification rules', succeeded: true };
    } catch (error) {
      console.error('Error updating rules:', error);
      return { settingName: 'notification rules', succeeded: false };
    }
  };

  const isSaveDisabled = isPendingEmails || isPendingRules || (!emailsState.isDirty && !rulesState.isDirty);

  const handleSave = async () => {
    let emailsData: ComplianceEmailsOutput | undefined;
    let rulesData: ComplianceFormOutput | undefined;

    /* Validate every dirty half before firing anything, so a rejected half can't leave a
       sibling endpoint already applied. Each half has its own resolver, so an undefined
       capture means that half's callback never ran. */
    if (emailsState.isDirty) {
      await handleSubmitEmails((validated) => {
        emailsData = validated;
      })();
      if (!emailsData) return;
    }

    if (rulesState.isDirty) {
      await handleSubmitRules((validated) => {
        rulesData = validated;
      })();
      if (!rulesData) return;
    }

    const saves: Promise<ComplianceSaveResult>[] = [];

    if (emailsData) {
      saves.push(saveEmails(emailsData));
    }

    if (rulesData) {
      saves.push(saveRules(rulesData));
    }

    if (saves.length === 0) return;

    const failed = (await Promise.all(saves)).filter((result) => !result.succeeded);

    if (failed.length === 0) {
      toast.success('Notification preferences updated successfully');
      return;
    }

    toast.error(failed.length === 1 ? `Failed to update ${failed[0].settingName}` : 'Some changes could not be saved');
  };

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void handleSave();
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-base font-medium">Documents Notifications</h2>
        <p className="text-default-text text-sm">
          Document expiry notifications are sent when a document approaches its expiration date. Notifications are sent according to the rules defined below.
        </p>
      </div>

      <p className="text-default-text text-sm">
        Notification rules are cumulative: if a document type matches more than one rule then all the matching rules will be applied and you may receive
        multiple notifications for the same document type.
      </p>

      <form onSubmit={handleFormSubmit}>
        <div className="flex w-full items-start gap-4">
          {/* 300px label column is the settings field-row width from the design; no spacing token matches it */}
          <p className="text-default-text w-[300px] shrink-0 text-sm">Email addresses</p>
          <div className="flex min-w-px flex-1 flex-col gap-2">
            <Controller
              control={emailsControl}
              name="emails"
              render={({ field: { value, onChange } }) => (
                <CreatableMultiSelect<{ label: string; value: string }>
                  value={(value ?? []).map((email) => ({ label: email, value: email }))}
                  onChange={(selected) => {
                    const emails = selected ? selected.map((item) => item.value) : [];
                    onChange(emails);
                  }}
                  onCreateOption={(inputValue) => {
                    if (Utils.ValidateEmail(inputValue) && !(value ?? []).includes(inputValue)) {
                      onChange([...(value ?? []), inputValue]);
                    }
                  }}
                  formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                  placeholder="Enter emails..."
                  noOptionsMessage={() => 'Type an email address and press Enter'}
                  isValidNewOption={(inputValue) => {
                    return Utils.ValidateEmail(inputValue) && !(value ?? []).includes(inputValue);
                  }}
                  options={[]}
                  isDisabled={isPendingEmails || isPendingRules || !canEditWorkspaceSettings}
                />
              )}
            />
          </div>
        </div>
      </form>

      <form className="flex flex-col gap-2" onSubmit={handleFormSubmit}>
        {fields.map((field, index) => (
          <ComplianceRuleCard
            key={field.id}
            control={rulesControl}
            index={index}
            documentCategories={documentCategories}
            onDelete={() => remove(index)}
            onClone={() => insert(index, fields[index])}
            disabled={isPendingEmails || isPendingRules || !canEditWorkspaceSettings}
          />
        ))}

        {fields.length === 0 && <p className="text-secondary-text text-sm">No notification rules have been created yet.</p>}

        {canEditWorkspaceSettings && (
          <Button onClick={() => append({ interval: 30 })} type="button" className="w-fit" disabled={isPendingRules} icon={<PlusIcon />}>
            Add Rule
          </Button>
        )}
      </form>

      {canEditWorkspaceSettings && (
        <Button type="button" variant="success" className="w-fit" onClick={handleSave} disabled={isSaveDisabled} loading={isPendingEmails || isPendingRules}>
          Save changes
        </Button>
      )}
    </div>
  );
}

function ComplianceRuleCard({
  control,
  index,
  documentCategories,
  onDelete,
  onClone,
  disabled,
}: {
  control: ReturnType<typeof useForm<ComplianceFormInput, unknown, ComplianceFormOutput>>['control'];
  index: number;
  documentCategories: CategoryDTO[];
  onDelete: () => void;
  onClone: () => void;
  disabled: boolean;
}) {
  const categoryOptions = documentCategories.map((category) => ({
    label: category.name,
    value: category.id,
  }));

  const periodOptions = PERIOD_OPTIONS.map((v) => ({
    label: v.toString(),
    value: v.toString(),
  }));

  return (
    <div className="flex flex-col gap-2 rounded border p-4">
      <div className="flex items-start justify-between">
        <span className="block text-sm">Notify me when:</span>
        <div className="flex items-center gap-2">
          <Button type="button" onClick={onClone} disabled={disabled} variant="ghost" className="p-0" aria-label="Duplicate rule">
            <CloneIcon />
          </Button>

          <Button type="button" onClick={onDelete} disabled={disabled} variant="ghost" className="text-default-red p-0" aria-label="Delete rule">
            <DeleteIcon />
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Controller
          control={control}
          name={`rules.${index}.categoryId` as const}
          render={({ field: { value, onChange } }) => (
            <Select
              aria-label="Document category"
              value={(value && categoryOptions.find((option) => option.value === value)) || { label: 'Any', value: '' }}
              onChange={(option) => onChange(option?.value || undefined)}
              options={[{ label: 'Any', value: '' }, ...categoryOptions]}
              isDisabled={disabled}
              className="min-w-[120px]"
            />
          )}
        />

        <span className="text-sm">documents for any asset will expire in</span>

        <Controller
          control={control}
          name={`rules.${index}.interval` as const}
          render={({ field: { value, onChange } }) => (
            <Select
              aria-label="Interval"
              value={{ label: String(value), value: String(value) }}
              onChange={(option) => onChange(parseInt(option?.value || '30'))}
              options={periodOptions}
              isDisabled={disabled}
              className="min-w-[80px]"
            />
          )}
        />

        <Controller
          control={control}
          name={`rules.${index}.interval` as const}
          render={({ field: { value } }) => <span className="text-sm">day{value === 1 ? '' : 's'}</span>}
        />
      </div>
    </div>
  );
}

const inspectionSchema = z.object({
  inspection: z.boolean().default(false),
});
type InspectionInput = z.input<typeof inspectionSchema>;
type InspectionOutput = z.output<typeof inspectionSchema>;

function NotificationsInspection() {
  const { org, setOrg } = useUserStore();
  const toast = useToast();
  const { canEditWorkspaceSettings } = usePermissions();

  const form = useForm<InspectionInput, unknown, InspectionOutput>({
    resolver: zodResolver(inspectionSchema),
    defaultValues: { inspection: org?.notifyInspection || false },
    mode: 'onChange',
  });

  const { control, handleSubmit, formState, reset } = form;

  const { data: inspectionData, isLoading: loading } = useQuery(getLegacyInspectionNotificationsQueryOptions());

  useEffect(() => {
    if (inspectionData) {
      reset({ inspection: inspectionData.inspection });
    }
  }, [inspectionData, reset]);

  const { mutateAsync: mutateInspectionAsync, isPending: isPendingInspection } = useMutation({
    mutationFn: async (inspectionValue: boolean) => {
      return (await (await ApiService.UpdateNotifyInspection({ inspection: inspectionValue })).json()).inspection;
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      const result = await mutateInspectionAsync(data.inspection);
      reset({ inspection: result });
      setOrg({ ...org!, notifyInspection: result });
      toast.success('Notification preferences updated successfully');
    } catch (error) {
      toast.error('Failed to update inspection notifications');
      console.error('Error updating inspection settings:', error);
    }
  });

  return (
    <form className="flex flex-col gap-4" onSubmit={onSubmit}>
      <div className="flex flex-col gap-1">
        <h2 className="text-base font-medium">Inspection Notifications</h2>
        <p className="text-default-text text-sm">The settings below allow you to control when email notifications are sent to the specified addresses.</p>
      </div>

      <div className="flex w-full items-center gap-4">
        {/* 500px label column is this screen's field-row width from the design; the width is set per screen, not shared */}
        <Label htmlFor="notify-inspection" className="text-default-text w-[500px] shrink-0 text-sm font-normal">
          Send daily inspection submissions to location contact email addresses
        </Label>
        <div className="flex items-center gap-2">
          <Controller
            control={control}
            name="inspection"
            render={({ field: { value, onChange } }) => (
              <Switch
                id="notify-inspection"
                checked={value}
                onCheckedChange={onChange}
                disabled={loading || isPendingInspection || !canEditWorkspaceSettings}
              />
            )}
          />
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" aria-label="More information">
                  <QuestionIcon className="text-default-text h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipPortal>
                <TooltipContent className="max-w-[290px]">
                  <TooltipArrow className="fill-white drop-shadow-[0_1px_0_rgb(var(--grey-3))]" />
                  Set the email address of the recipient in the location summary screen.
                </TooltipContent>
              </TooltipPortal>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {canEditWorkspaceSettings && (
        <Button
          type="submit"
          variant="success"
          className="w-fit"
          disabled={isPendingInspection || !formState.isValid || formState.isSubmitting || !formState.isDirty}
          loading={isPendingInspection}>
          Save changes
        </Button>
      )}
    </form>
  );
}

export default SettingsNotificationsPage;
